import {sessionForGroup, fetchSnapshot, groupHasMember, groupByHandle, writeGroup, hydrate} from './product-switcher';
import {GROUP_TYPE, emptySwitcher, objectFingerprint, reconcileSwitcher, switcherFromMetaobject, switcherFields} from '../product-switcher.js';

export async function linkProductGroup(session: any, groupId: string) {
  if (session.published?.id === groupId) return session;
  const product = session.product;
  const group = await fetchSnapshot(groupId);
  if (group.type !== GROUP_TYPE || !groupHasMember(group, product.id) || switcherFromMetaobject(group).detached || group.handle.startsWith('evp-switcher-draft-')) throw new Error('Choose a group that contains this product.');
  const next = await sessionForGroup(product, group, {productGroupHandle: group.handle});
  return {...next, savedHandle: session.savedHandle, needsSave: true};
}

export async function createProductGroup(session: any, creationId: string) {
  const product = session.product;
  const value = emptySwitcher(product);
  value.groupId = creationId;
  value.products = [{productId: product.id, title: product.title, handle: product.handle, label: product.title}];
  let group = await groupByHandle(product.handle);
  const handle = group && switcherFromMetaobject(group).groupId !== creationId ? `${product.handle.slice(0, 190)}-${creationId}` : product.handle;
  if (handle !== product.handle) group = await groupByHandle(handle);
  if (group && (switcherFromMetaobject(group).groupId !== creationId || !groupHasMember(group, product.id))) throw new Error('The new group handle is already in use.');
  if (!group) group = await writeGroup(value, handle, true);
  const next = await sessionForGroup(product, group, {productGroupHandle: group.handle});
  return {...next, savedHandle: session.savedHandle, needsSave: true};
}

export async function saveGroupMembers(session: any, members: any[]) {
  const product = session.product;
  if (!session.published) throw new Error('Link a group first.');
  const group = await fetchSnapshot(session.published.id);
  if (objectFingerprint(group) !== objectFingerprint(session.published)) throw new Error('The shared group changed. Reload before saving members.');
  const ids = members.map((member) => member.productId);
  if (!ids.length || ids.length > 5 || new Set(ids).size !== ids.length || ids.some((id) => !/^gid:\/\/shopify\/Product\/\d+$/.test(id))) throw new Error('Choose between one and five unique products.');
  const hydrated = (await hydrate({products: members})).products;
  if (hydrated.some((member: any) => !member.handle)) throw new Error('A member is missing or inaccessible.');
  // Keep the Liquid projection coherent, preserving labels/order and unrelated JSON.
  // No product references or drafts are changed by a membership save.
  const value = reconcileSwitcher(switcherFromMetaobject(group), hydrated);
  const config = JSON.parse(group.fields.find((field: any) => field.key === 'configuration')?.value || '{}');
  const nextConfig = JSON.parse(switcherFields(value)[2].value);
  nextConfig.products = nextConfig.products.map((item: any) => ({...config.products?.find((existing: any) => existing.productId === item.productId), ...item}));
  await writeGroup(value, group.handle, false, [
    {key: 'members', value: JSON.stringify(ids)},
    {key: 'configuration', value: JSON.stringify({...config, ...nextConfig, revision: crypto.randomUUID()})},
  ]);
  return sessionForGroup(product, await fetchSnapshot(group.id), {productGroupHandle: group.handle});
}
